import { normalizeRing } from './domain'
import type { BirdFamily, BirdParentSummary, BirdSearchResult, FamilyRecord, PedigreeGeneration, PedigreeNode } from './types'

function parentRing(record: FamilyRecord, role: 'padre' | 'madre') {
  return normalizeRing(record[role].anilla)
}

function uniqueRecords(records: FamilyRecord[]) {
  const seen = new Set<string>()
  return records.filter(record => {
    const key = `${record.source}:${record.birdId}:${record.sheetId}:${record.anio}:${record.hoja}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function buildBirdFamily(ring: string, records: FamilyRecord[]): BirdFamily {
  const targetRing = normalizeRing(ring)
  const targets = records.filter(record => normalizeRing(record.anilla) === targetRing)
  const direct: FamilyRecord[] = []
  const paternal: FamilyRecord[] = []
  const maternal: FamilyRecord[] = []

  records.forEach(candidate => {
    const candidateRing = normalizeRing(candidate.anilla)
    if (!candidateRing || candidateRing === targetRing) return
    let relation: 'direct' | 'paternal' | 'maternal' | undefined
    for (const target of targets) {
      const targetFather = parentRing(target, 'padre')
      const targetMother = parentRing(target, 'madre')
      const candidateFather = parentRing(candidate, 'padre')
      const candidateMother = parentRing(candidate, 'madre')
      const sameFather = Boolean(targetFather && candidateFather && targetFather === candidateFather)
      const sameMother = Boolean(targetMother && candidateMother && targetMother === candidateMother)
      if (sameFather && sameMother) { relation = 'direct'; break }
      if (sameFather && targetMother && candidateMother && targetMother !== candidateMother) relation ||= 'paternal'
      if (sameMother && targetFather && candidateFather && targetFather !== candidateFather && relation !== 'paternal') relation = 'maternal'
    }
    if (relation === 'direct') direct.push(candidate)
    else if (relation === 'paternal') paternal.push(candidate)
    else if (relation === 'maternal') maternal.push(candidate)
  })

  return {
    anilla: targetRing,
    targets: uniqueRecords(targets),
    directSiblings: uniqueRecords(direct),
    paternalSiblings: uniqueRecords(paternal),
    maternalSiblings: uniqueRecords(maternal)
  }
}
function comparableName(value: string) {
  return value.trim().toLocaleUpperCase('es-ES')
}

function bestRecordForRing(ring: string, records: FamilyRecord[]) {
  const normalized = normalizeRing(ring)
  return records
    .filter(record => normalizeRing(record.anilla) === normalized)
    .sort((left, right) => {
      const score = (record: FamilyRecord) =>
        Number(Boolean(parentRing(record, 'padre'))) * 4 + Number(Boolean(parentRing(record, 'madre'))) * 4 +
        Number(Boolean(record.padre.nombre)) + Number(Boolean(record.madre.nombre)) + Number(record.source === 'APP')
      return score(right) - score(left)
    })[0]
}

function pedigreeNode(summary: BirdParentSummary, generation: number, path: string, relation: string, records: FamilyRecord[]): PedigreeNode {
  const anilla = normalizeRing(summary.anilla)
  const record = anilla ? bestRecordForRing(anilla, records) : undefined
  return {
    path,
    relation,
    generation,
    anilla,
    nombre: summary.nombre || record?.nombre || '',
    plumaje: summary.plumaje || record?.plumaje || '',
    sexo: record?.sexo || '',
    deceased: record?.deceased || false,
  }
}

export function buildPedigree(ring: string, records: FamilyRecord[], depth = 3): PedigreeGeneration[] {
  const normalized = normalizeRing(ring)
  const rootRecord = bestRecordForRing(normalized, records)
  const root: PedigreeNode = {
    path: 'ROOT', relation: 'PALOMO', generation: 0, anilla: normalized,
    nombre: rootRecord?.nombre || '', plumaje: rootRecord?.plumaje || '', sexo: rootRecord?.sexo || '', deceased: rootRecord?.deceased || false,
  }
  const titles = ['Palomo', 'Padres', 'Abuelos', 'Bisabuelos']
  const generations: PedigreeGeneration[] = [{ title: titles[0], nodes: [root] }]
  let current = [root]
  for (let generation = 1; generation <= depth; generation += 1) {
    const next: PedigreeNode[] = []
    current.forEach((node, parentIndex) => {
      const record = node.anilla ? bestRecordForRing(node.anilla, records) : undefined
      const fatherRelation = generation === 1 ? 'PADRE' : generation === 2 ? (parentIndex === 0 ? 'ABUELO PATERNO' : 'ABUELO MATERNO') : 'BISABUELO'
      const motherRelation = generation === 1 ? 'MADRE' : generation === 2 ? (parentIndex === 0 ? 'ABUELA PATERNA' : 'ABUELA MATERNA') : 'BISABUELA'
      const empty = { nombre: '', anilla: '', plumaje: '' }
      next.push(pedigreeNode(record?.padre || empty, generation, `${node.path}-P`, fatherRelation, records))
      next.push(pedigreeNode(record?.madre || empty, generation, `${node.path}-M`, motherRelation, records))
    })
    generations.push({ title: titles[generation] || `Generación ${generation}`, nodes: next })
    current = next
  }
  return generations
}
export function findChildrenForBird(bird: BirdSearchResult, records: FamilyRecord[]) {
  const ring = normalizeRing(bird.anilla)
  const name = comparableName(bird.nombre)
  if (!ring && !name) return []
  return uniqueRecords(records.filter(record => {
    if (ring) return parentRing(record, 'padre') === ring || parentRing(record, 'madre') === ring
    return comparableName(record.padre.nombre) === name || comparableName(record.madre.nombre) === name
  }))
}